/**
 * Used by filterless categories
 */

import site from "../site";
import Helpers from "./helpers";
import SelectorFilter from "./selector-filter";
import ToggleBlock from "./toggle-block";
import Tooltip from "./tooltip";
import Tracking from "./tracking";

declare var Mustache: any;
declare var showMore: any;
declare var popups: any;
declare var dataLayer: any;

export default class Filters {

	private filtersFormElement: HTMLFormElement;
	private usePush = true;
	private firstLoad = true;
	private filterlessCategoryMode = document.querySelectorAll('[data-page-template="FilterlessCategory"]').length;
	private mobileFiltersState: any[] = [];
	private lastChangedInput: HTMLInputElement = null;
	private productsAbortController: AbortController = null;
	private filtersAbortController: AbortController = null;
	private toggleBlockCollapsedCSSClass = 'collapsed';
	private lastDataFromUrl = "";

	constructor(filtersFormElement: HTMLFormElement) {
		this.filtersFormElement = filtersFormElement;
		this.setup();
	}

	private setup() {
		if (this.firstLoad) {
			const dataFromUrl = this.getDataFromUrl();
			this.lastDataFromUrl = dataFromUrl;
			this.ajaxUpdate(this.filtersFormElement, dataFromUrl, true);
			this.firstLoad = false;
		}
		this.attachEvents();
	}

	private getDataFromUrl() {
		const currentUrl = window.location.search;
		const sort = "sort=" + this.getSortInput().value;
		const seperator = currentUrl.indexOf('?') > -1 ? '&' : '?';
		return currentUrl + seperator + sort;
	}

	private attachEvents() {

		const debouncedTagsResize = Helpers.debounce(this.setFilterTagsPosition, 250);
		const self = this;
		window.addEventListener('resize', debouncedTagsResize.bind(self));

		// Checkboxes and radios
		this.filtersFormElement.addEventListener('change', (e: Event) => {
			const input = e.target as HTMLInputElement;
			// Go back to page 1 when filters change, except for page input
			if (input.getAttribute("name") !== 'page') {
				const pageInputElement = this.getPageInput();
				pageInputElement.value = "1";
			}
			this.lastChangedInput = input;
			this.triggerFiltersUpdate();
		});

		document.querySelector('.js-filter-page').addEventListener('change', (e: Event) => {

			const targetElement = e.target as HTMLSelectElement;

			if (targetElement.matches('select[name="sort"]')) {
				const sortInput = this.filtersFormElement.querySelector('input[name="sort"]') as HTMLInputElement;
				sortInput.value = targetElement.value;
				this.triggerFiltersUpdate();
			}

			if (targetElement.matches('select[name="page"]')) {
				const pageInput = this.getPageInput();
				pageInput.value = targetElement.value;
				this.triggerFiltersUpdate();
			}

		});

		document.querySelector('.js-filter-page').addEventListener('click', (e: Event) => {

			const targetElement = e.target as HTMLElement;

			// Pagination
			if (targetElement.matches('.js-pager-next')) {
				this.changePage(parseInt(this.getCurrentPageFromURL()) + 1);
				e.preventDefault();
				return false;
			}

			if (targetElement.matches('.js-pager-previous')) {
				this.changePage(parseInt(this.getCurrentPageFromURL()) - 1);
				e.preventDefault();
				return false;
			}

			if (targetElement.matches('.js-pager-page')) {
				this.changePage(targetElement.textContent);
				e.preventDefault();
			}

			// Applied filter tags
			if (targetElement.matches('.js-applied-filter')) {
				const relatedFacet = document.getElementById(targetElement.getAttribute('data-facet-id')) as HTMLInputElement;
				relatedFacet.checked = false;
				this.triggerFiltersUpdate();
				e.preventDefault();
			}

			// Clear all above e.g. ratings
			if (targetElement.matches('.js-clear-above')) {
				const group = targetElement.closest('.content');
				this.unCheckCheckboxes(group);
				this.triggerFiltersUpdate();
				targetElement.classList.add('filterClearAboveDone');
				e.preventDefault();
			}

			// Clear all filters
			if (targetElement.classList.contains('clearAllFilters')) {
				this.unCheckCheckboxes(this.filtersFormElement);
				this.triggerFiltersUpdate();
				e.preventDefault();
			}

			// Skip to results accessibility link
			if (targetElement.classList.contains('js-filter-skip-to-results')) {
				e.preventDefault();
				this.closeFilterSlideOutIfOpen();
				this.focusOnFirstResult();
			}

			// Track product link clicks
			if (targetElement.classList.contains('js-product-link')) {
				this.trackSelectItem(targetElement);
			}

		});

		window.addEventListener('locationchange', () => {
			const currentDataFromUrl = this.getDataFromUrl();
			if (this.lastDataFromUrl !== currentDataFromUrl) {
				this.lastDataFromUrl = currentDataFromUrl;
				this.ajaxUpdate(this.filtersFormElement, currentDataFromUrl);
			}
		});

	}

	private focusOnFirstResult() {
		const firstResult = document.querySelector('#rangeContentWrapper .js-product a') as HTMLLinkElement;
		if (firstResult) {
			firstResult.focus();
		}
	}

	private closeFilterSlideOutIfOpen() {
		const slideOutElement = document.querySelector('.js-filter-slide-out') as HTMLElement;
		const closeButton = document.querySelector('.js-filter-slide-close') as HTMLButtonElement;
		const isSlideOutOpen = slideOutElement
			&& (slideOutElement.classList.contains('active') || slideOutElement.classList.contains('is-active'));

		if (!isSlideOutOpen || !closeButton) {
			return;
		}

		closeButton.click();
	}

	private getCurrentPageFromURL() {
		let page = Helpers.getParameter('page');
		if (!page) {
			page = "1";
		}
		return page;
	}

	private triggerFiltersUpdate() {
		const formData = '?' + this.serializeFormData();
		if (this.usePush) {
			history.pushState({ form: formData }, this.getTitle(), window.location.pathname + formData);
		}
		else {
			history.replaceState(null, null, window.location.pathname + formData);
		}
	}

	private setIfFiltersShouldBeScrollable() {

		const filterContentElements = this.filtersFormElement.querySelectorAll('.js-filter-content');

		for (let i = 0, len = filterContentElements.length; i < len; i++) {
			const wrapper = filterContentElements[i];
			if (wrapper.querySelectorAll('.level1').length > 6) {
				wrapper.classList.add('scrollable');
			}
			wrapper.classList.add('filterScrollableDone');
		}

	}

	private saveMobileFiltersOpenState() {

		this.mobileFiltersState = [];

		const toggleBlockElements = this.filtersFormElement.querySelectorAll('.js-toggle-block');

		for (let i = 0, len = toggleBlockElements.length; i < len; i++) {
			const currentToggleBlock = toggleBlockElements[i];
			this.mobileFiltersState.push({
				name: currentToggleBlock.querySelector('[data-toggle-value]').getAttribute('data-toggle-value'),
				isCollapsed: currentToggleBlock.querySelector('.js-toggle-title').classList.contains(this.toggleBlockCollapsedCSSClass),
				showMoreOpen: currentToggleBlock.querySelector('.mShowMore .link') ? currentToggleBlock.querySelector('.mShowMore .link').classList.contains('less') : false
			});
		}

	}

	private setMobileFiltersOpenState() {

		for (let i = 0, len = this.mobileFiltersState.length; i < len; i++) {
			const currentFilter = this.mobileFiltersState[i];
			const currentFilterToggleValue = this.filtersFormElement.querySelector('[data-toggle-value="' + currentFilter.name + '"]');
			if (!currentFilterToggleValue) {
				continue;
			}
			const currentFilterContainer = currentFilterToggleValue.closest('.js-toggle-block');
			const toggleTitle = currentFilterContainer.querySelector('.js-toggle-title');
			const toggleContent = currentFilterContainer.querySelector('.js-toggle-content');
			const stateInput = currentFilterContainer.querySelector('.js-toggle-expanded') as HTMLInputElement;
			const isExpanded = !currentFilter.isCollapsed;
			if (currentFilter.isCollapsed) {
				toggleTitle.classList.add(this.toggleBlockCollapsedCSSClass);
				toggleContent.classList.add(this.toggleBlockCollapsedCSSClass);
			} else {
				toggleTitle.classList.remove(this.toggleBlockCollapsedCSSClass);
				toggleContent.classList.remove(this.toggleBlockCollapsedCSSClass);
			}
			toggleTitle.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
			if (stateInput) {
				stateInput.value = isExpanded ? currentFilter.name : '';
			}
			if (currentFilter.showMoreOpen) {
				const showMoreLink = currentFilterContainer.querySelector('.mShowMore .link') as HTMLLinkElement;
				if (showMoreLink) {
					showMoreLink.click();
				}
			}
		}

	}

	private unCheckCheckboxes(container: Element) {
		const checkboxes = container.querySelectorAll('input');
		for (let i = 0, len = checkboxes.length; i < len; i++) {
			checkboxes[i].checked = false;
			checkboxes[i].removeAttribute('checked');
		}
	}

	private getTitle() {
		return document.title;
	}

	private getPageInput() {
		return this.filtersFormElement.querySelector('input[name="page"]') as HTMLInputElement
	}

	private getSortInput() {
		return this.filtersFormElement.querySelector('input[name="sort"]') as HTMLInputElement
	}

	private changePage(page: any) {
		const pageInput = this.getPageInput();
		pageInput.value = page;
		this.triggerFiltersUpdate();
	}

	private ajaxUpdate(form: HTMLElement, formData: string, firstLoad = false) {

		// change the class so category, subcategory, brand and range all behave the save once filters are applied
		var wrapper = document.querySelector('#rangeContentWrapper'),
		wrapperClass = 'mRange';
		if (wrapper && !firstLoad) {
			if (wrapper.matches('.mRange')) { wrapperClass = wrapperClass + ' mRange'; }
			if (wrapper.matches('.mRange--no-filters')) { wrapperClass = wrapperClass + ' mRange--no-filters'; }
			wrapper.setAttribute('class', wrapperClass);
		}

		// the list is the bit that needs updating on the page
		var productResultsTargetElement = document.querySelector('#rangeContentWrapper .inner .content .list') as HTMLElement;

		this.saveMobileFiltersOpenState();

		// scroll up
		if (!firstLoad) {
			const scrollAfterDataElement = document.querySelector('.js-filter-scroll-after-change') as HTMLElement;
			const elementToScrollTo = scrollAfterDataElement ? scrollAfterDataElement : document.getElementById('content') as HTMLElement;
			const scrollOffset = Helpers.getOffset(elementToScrollTo).top;
			const scrollOffsetVariant = window.innerWidth < 640 ? 50 : 100; // 640 matches the $bpTabletLarge breakpoint
			if (window.scrollY < scrollOffset - scrollOffsetVariant || site.win.scrollTop() > scrollOffset + scrollOffsetVariant) {
				Helpers.scrollTo(scrollOffset, site.transitionSpeed * 2);
			}
		}

		if (!firstLoad) {
			popups.loader();
			if (productResultsTargetElement) {
				productResultsTargetElement.style.display = 'none';
			}
		}

		// get the data
		this.getProducts(form, formData, productResultsTargetElement, firstLoad);
		this.getFilters(form, formData, productResultsTargetElement, firstLoad);

	}

	private getProducts(form: any, formData: string, productResultsTargetElement: HTMLElement, firstLoad: boolean) {

		// abort previous requests
		if (this.productsAbortController) {
			this.productsAbortController.abort();
		}
		this.productsAbortController = new AbortController();
		const signal = this.productsAbortController.signal;

		fetch(this.formatRequestUrl(form.getAttribute('data-products-json-url'), formData), {
			signal: signal
		})
			.then((response: any) => {
				if (response.ok) {
					return response.json();
				}
				else {
					throw new Error(response as any);
				}
			}).then((data) => {

				this.removeError();

				this.templateProductResults(data, productResultsTargetElement, firstLoad);

				// hide loader
				popups.allow = true;
				popups.close();

				if (productResultsTargetElement && !firstLoad) {
					productResultsTargetElement.style.display = 'grid';
				}

				if (this.filterlessCategoryMode) this.scrollToTop();

				// reinitialise components
				Tooltip.init(false);

				if (window.matchMedia('(max-width: 980px)').matches) {
					if (this.lastChangedInput && this.lastChangedInput.closest('.mFilter')) {
						this.lastChangedInput.closest('.mFilter').scrollIntoView();
					}
				}

				this.productsAbortController = null;

				if (!firstLoad && Helpers.isUsingKeyboard()) {
					this.focusOnFirstResult();
				}

				this.trackListView(data);

			}).catch(() => {
				this.showRequesError(productResultsTargetElement);
			});
	}

	private getFilters(form: any, formData: string, productResultsTargetElement: HTMLElement, firstLoad: boolean) {

		// abort previous requests
		if (this.filtersAbortController) {
			this.filtersAbortController.abort();
		}
		this.filtersAbortController = new AbortController();
		const signal = this.filtersAbortController.signal;

		this.setFiltersLoading();

		fetch(this.formatRequestUrl(form.getAttribute('data-facets-json-url'), formData), {
			signal: signal
		})
			.then((response: any) => {
				if (response.ok) {
					return response.json();
				}
				else {
					throw new Error(response as any);
				}
			}).then((data) => {

				if (firstLoad && !this.isDesktopLayout()) {
					data = this.setFiltersToClosed(data);
				}

				this.templateFilterResults(data, firstLoad);
				this.setMobileFiltersOpenState();
				Tooltip.init(false);

				this.showMoreToggles();
				new ToggleBlock();

				this.setFilterTagsPosition();
				this.setIfFiltersShouldBeScrollable();

				this.setFiltersLoaded();

				const triggerUpdateWishlistButtons = Helpers.createCustomEventForDispatch('updateWishlistButtons');
				document.dispatchEvent(triggerUpdateWishlistButtons);

			}).catch(() => {
				this.showRequesError(productResultsTargetElement);
			});

	}

	/**
	 * This is needed for the filters/load more ABCD test to make sure the filters match live behaviour
	 * */
	private setFiltersToClosed(data) {
		for (var i = 0; i < data.Filters.length; i++) {
			data.Filters[i].IsExpanded = false;
		}
		return data;
	}

	private setFiltersLoading() {
		const filters = document.querySelectorAll('.mFilter');
		for (var i = 0; i < filters.length; i++) {
			filters[i].classList.add('mFilter--placeholder');
		}
	}

	private setFiltersLoaded() {
		const filters = document.querySelectorAll('.mFilter');
		for (var i = 0; i < filters.length; i++) {
			filters[i].classList.remove('mFilter--placeholder');
		}
	}

	private showRequesError(productResultsTargetElement: HTMLElement) {
				this.removeError();
				productResultsTargetElement.insertAdjacentHTML('beforebegin', '<p class="formError js-filters-error">Sorry, there was an error when filtering. Please try again.</p>')
				popups.loader(true);
				const popupFadeElement = document.getElementById('popupFade') as HTMLDivElement;
				if (popupFadeElement) {
					popupFadeElement.style.display = 'none';
				}
				if (productResultsTargetElement) {
					productResultsTargetElement.style.display = 'block';
				}
	}

	private formatRequestUrl(baseUrl: string, formData: string) {
		if (baseUrl.indexOf('?') !== -1 && (formData != null && formData.indexOf('?') === 0)) {
			formData = formData.substring(1);
		}
		return baseUrl + '&' + formData;
	}

	private templateProductResults(data: any, productResultsTargetElement: HTMLElement, firstLoad: boolean) {

		if (!firstLoad) {
			const productTemplate = document.getElementById('productsTemplate').innerHTML;
			if (productResultsTargetElement) {
				productResultsTargetElement.innerHTML = Mustache.render(productTemplate, data);
			}


			const pagerTemplate = document.getElementById('pagerTemplate').innerHTML;
			const pagerElements = document.querySelectorAll('.mPager');
			if (pagerElements.length) {
				for (var i = 0; i < pagerElements.length; i++) {
					pagerElements[i].innerHTML = Mustache.render(pagerTemplate, data.PageMeta);
				}
			}

			const filterTotalTemplate = document.getElementById('filterTotalTemplate');
			const filterSubmitElement = document.querySelector('.filterSubmit');
			if (filterSubmitElement && filterTotalTemplate) {
				filterSubmitElement.innerHTML = Mustache.render(filterTotalTemplate.innerHTML, data);
		}

		}

	}

	private templateFilterResults(data: any, firstLoad: boolean) {
		if (this.filterlessCategoryMode) {
			return;
		}

		let filtersHTML = '';
		for (var i = 0; i < data.Filters.length; i++) {
			const template = document.getElementById(data.Filters[i].Template + 'Template').innerHTML;
			filtersHTML += Mustache.render(template, data.Filters[i]);
		}

		const filtersElement = this.filtersFormElement.querySelector('.filters');
		if (filtersElement) {
			filtersElement.innerHTML = filtersHTML;
		}

		const appliedFiltersTemplate = document.getElementById('AppliedFiltersTemplate').innerHTML;
		const appliedFiltersElement = document.getElementById('appliedFilters');
		if (appliedFiltersElement) {
			appliedFiltersElement.innerHTML = Mustache.render(appliedFiltersTemplate, data);
		}
			}

	private removeError() {
		const filtersErrorElement = document.querySelector('.js-filters-error');
		if (filtersErrorElement) {
			filtersErrorElement.parentNode.removeChild(filtersErrorElement);
		}
	}

	private setFilterTagsPosition() {

		const appliedFiltersElement = document.querySelector('.js-applied-filters-container') as HTMLElement;

		if (!appliedFiltersElement) {
			return;
		}

		const filtersInMain = !this.filtersFormElement.querySelector('.js-applied-filters-container');

		if (this.isDesktopLayout()) {
			if (filtersInMain) {
				this.prependTo(appliedFiltersElement, this.filtersFormElement);
			}
		}
		else {
			if (!filtersInMain) {
				this.prependTo(appliedFiltersElement, document.querySelector('#appliedFiltersWrapper'));
			}
		}

		const appliedFiltersInner = document.querySelector('.js-applied-filters-inner');

		if (appliedFiltersInner) {
			appliedFiltersInner.classList.add('mFilterTags--initialized');
		}

	}

	private isDesktopLayout() {
		return matchMedia('(min-width: 980px)').matches;
	}

	private prependTo(containerToAppend: HTMLElement, targetContainer: HTMLElement) {
		if (containerToAppend.parentElement) {
			containerToAppend.parentElement.removeChild(containerToAppend);
		}
		targetContainer.insertBefore(containerToAppend, targetContainer.firstChild)
	}

	private scrollToTop() {

		const scrollOffset = Helpers.getOffset(document.querySelector('.js-filter-page'));
		if (site.win.scrollTop() > scrollOffset) {
			Helpers.scrollTo(scrollOffset.top, site.transitionSpeed * 2);
		}

	}

	private serializeFormData() {

		const serialized = [];
		const elements = this.filtersFormElement.querySelectorAll('input,select');

		for (var i = 0; i < elements.length; i++) {

			let field = elements[i] as any;

			if (!field.name ||
				field.disabled ||
				field.type === 'file' ||
				field.type === 'reset' ||
				field.type === 'submit' ||
				field.type === 'button' ||
				field.name === 'expanded' ||
				!field.value
			) { continue; }

			if (field.type === 'select-multiple') {
				for (var j = 0; j < field.options.length; j++) {
					if (!field.options[j].selected) continue;
					serialized.push(encodeURIComponent(field.name) + "=" + encodeURIComponent(field.options[j].value));
				}

			} else if ((field.type !== 'checkbox' && field.type !== 'radio') || field.checked) {
				serialized.push(encodeURIComponent(field.name) + "=" + encodeURIComponent(field.value));
			}

		}

		return serialized.join('&');
	}

	private trackListView(data: any) {
		let items = [];
		for (var i = 0; i < data.Products.length; i++) {
			const currentLineItem = data.Products[i];
			items.push({
				item_id: currentLineItem.Name,
				item_name: currentLineItem.DisplayName,
				price: currentLineItem.Price.Price
			});
		}
		Tracking.trackListView("category_results", "Category Results", items);
	}

	private trackSelectItem(element: HTMLElement) {
		const elementParent = element.closest('.js-product') as HTMLDivElement;
		const item = Tracking.getItemDetails(elementParent);
		Tracking.trackSelectItem("category_results", "Category Results", item);
	}

	private showMoreToggles() {

		const checkboxesAndRadios = SelectorFilter.excludeByClass(document.querySelectorAll('.formChecks[data-limit], .formRadios[data-limit]'), 'moreDone');

		for (let i = 0; i < checkboxesAndRadios.length; i++) {

			const group = checkboxesAndRadios[i];
			const limit = parseInt(group.getAttribute('data-limit'), 10);
			const offset = limit + 0;
			const items = group.querySelectorAll('.level1');

			if (items.length <= offset) {
				continue;
			}

			if (group.getAttribute('data-expanded') !== 'true') {
				// hide over limit items
				for (let j = 0; j < items.length; j++) {
					const currentItem = items[j] as HTMLElement;
					if (j >= limit) {
						currentItem.style.display = "none";
					}
				}
				group.insertAdjacentHTML('afterend', '<p class="mShowMore"><button type="button" class="link">See More Options</button></p>');
			}
			else {
				group.insertAdjacentHTML('afterend', '<p class="mShowMore"><button type="button" class="link less">See Less Options</button></p>');
			}

			group.parentNode.querySelector('.mShowMore .link').addEventListener('click', function (e) {
				e.preventDefault();
				const link = e.target as HTMLLinkElement;
				if (link.classList.contains('less')) {
					// hide over limit items
					for (let j = 0; j < items.length; j++) {
						const currentItem = items[j] as HTMLElement;
						if (j >= limit) {
							currentItem.style.display = "none";
						}
					}
					link.textContent = 'See More Options';
					link.classList.remove('less');
				}
				else {
					// show over limit items
					for (let j = 0; j < items.length; j++) {
						const currentItem = items[j] as HTMLElement;
						if (j >= limit) {
							currentItem.style.display = "block";
						}
					}
					link.textContent = 'See Less Options';
					link.classList.add('less');
				}
			});

			group.classList.add('moreDone');
		}

	}

}
